import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import { canSeeAgents } from '../../../users/utils.ts';
import { GET_AGENT_MAILBOX_LINKS } from '../../assistants/queries.ts';

export interface AgentMailboxLink {
    id: string;
    name: string;
}

interface UseAgentMailboxLinksResult {
    agentByMailboxId: Map<string, AgentMailboxLink>;
    agentNameByMailboxId: Map<string, string>;
    agentNamesByMailboxId: Map<string, string[]>;
    agentOptions: { value: string; label: string }[];
    loading: boolean;
}

export const useAgentMailboxLinks = (): UseAgentMailboxLinksResult => {
    const { user } = useApplicationContext();
    const { data, loading } = useQuery(GET_AGENT_MAILBOX_LINKS, {
        fetchPolicy: 'cache-first',
        skip: !canSeeAgents(user),
    });
    const agents = data?.agents || [];

    const agentByMailboxId = useMemo(() => {
        const map = new Map<string, AgentMailboxLink>();
        for (const agent of agents) {
            for (const integration of agent.smartInboxIntegrations || []) {
                if (!map.has(integration.id)) {
                    map.set(integration.id, { id: agent.id, name: agent.name });
                }
            }
        }
        return map;
    }, [agents]);

    const agentNameByMailboxId = useMemo(() => {
        const map = new Map<string, string>();
        agentByMailboxId.forEach((agent, mailboxId) => map.set(mailboxId, agent.name));
        return map;
    }, [agentByMailboxId]);

    const agentNamesByMailboxId = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const agent of agents) {
            for (const integration of agent.smartInboxIntegrations || []) {
                const names = map.get(integration.id) || [];
                names.push(agent.name);
                map.set(integration.id, names);
            }
        }
        return map;
    }, [agents]);

    const agentOptions = useMemo(
        () => agents.map((agent: AgentMailboxLink) => ({ value: agent.id, label: agent.name })),
        [agents]
    );

    return { agentByMailboxId, agentNameByMailboxId, agentNamesByMailboxId, agentOptions, loading };
};

export default useAgentMailboxLinks;
